import { productImageAlt } from "@/lib/imageAlt";
import { Link } from "react-router-dom";
import { getShopifyImageUrl, ShopifyProduct, formatPrice } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cleanProductTitle } from "@/lib/productTitle";
import { firstAvailableVariant, variantLabel } from "@/lib/variants";
import { COMPLEMENTARY, keyOf } from "@/lib/complements";

type Variant = ShopifyProduct['node']['variants']['edges'][number]['node'];

interface BundleSectionProps {
  currentProduct: ShopifyProduct['node'];
  /** The variant the shopper has selected; the bundle adds exactly this one. */
  currentVariant?: Variant;
  recommendations: ShopifyProduct[];
}

function isCompatible(currentTitle: string, currentType: string | undefined, candidate: ShopifyProduct): boolean {
  const currentKey = keyOf(currentTitle, currentType);
  const candKey = keyOf(candidate.node.title, candidate.node.productType);
  if (!currentKey || !candKey) return false;
  if (currentKey === candKey) return false; // don't pair identical types (sofa + sofa)
  const allowed = COMPLEMENTARY[currentKey];
  return !!allowed && allowed.includes(candKey);
}

export const BundleSection = ({ currentProduct, currentVariant, recommendations }: BundleSectionProps) => {
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);

  // Only pieces that can be bought, each with its in-stock variant.
  const bundleItems = recommendations
    .filter((r) => isCompatible(currentProduct.title, currentProduct.productType, r))
    .map((p) => ({ product: p, variant: firstAvailableVariant(p.node) }))
    .filter((x): x is { product: ShopifyProduct; variant: Variant } => !!x.variant)
    .slice(0, 2);
  // A sold-out selection has no bundle: checkout would drop it from the cart.
  if (bundleItems.length < 1 || !currentVariant?.availableForSale) return null;

  const allItems = [{ product: { node: currentProduct } as ShopifyProduct, variant: currentVariant }, ...bundleItems].map(
    ({ product, variant }) => ({
      product,
      title: cleanProductTitle(product.node.title, product.node.productType),
      handle: product.node.handle,
      image: product.node.images?.edges?.[0]?.node.url,
      price: parseFloat(variant.price.amount),
      currencyCode: variant.price.currencyCode,
      variant,
      // The selected option of this page's product: its name may state another size.
      label: product.node.id === currentProduct.id ? variantLabel(currentProduct, variant) : "",
    }),
  );

  const subtotal = allItems.reduce((sum, item) => sum + item.price, 0);
  const currencyCode = allItems[0].currencyCode;

  const handleAddBundle = async () => {
    // The full product node, so the cart shows each line's image.
    // Count what Shopify really added: a sold-out piece is refused, and the cart store says so.
    let added = 0;
    for (const { product, variant } of allItems) {
      const result = await addItem({
        product,
        variantId: variant.id,
        variantTitle: variant.title || "",
        price: variant.price,
        quantity: 1,
        selectedOptions: variant.selectedOptions || [],
      });
      if (result.addedQuantity > 0) added++;
    }
    if (added === 0) return;
    toast.success("Added to cart", {
      description: `${added} item${added === 1 ? "" : "s"} added`,
      position: "top-center",
    });
  };

  return (
    <section className="border-t border-border">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="grid lg:grid-cols-[1fr,380px] gap-8 items-start">
          <div className="aspect-[16/10] overflow-hidden bg-[#f5f0eb] rounded-lg">
            {allItems[0].image && (
              <img src={getShopifyImageUrl(allItems[0].image, 600)} alt="Bundle preview" className="w-full h-full object-contain mix-blend-multiply" loading="lazy" decoding="async" />
            )}
          </div>

          <div>
            <h2 className="font-sans text-[22px] md:text-[24px] font-semibold text-foreground mb-2">
              Buy them together
            </h2>
            <p className="text-[15px] text-muted-foreground leading-relaxed mb-6">
              Pieces suggested to go with this one. Add them in one step — each at its normal price.
            </p>

            <div className="space-y-4 mb-6">
              {allItems.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Link to={`/product/${item.handle}`} className="flex-shrink-0">
                    <div className="w-[56px] h-[56px] bg-[#f5f0eb] rounded overflow-hidden">
                      {item.image && <img src={getShopifyImageUrl(item.image, 600)} alt={productImageAlt(item.title, { displayName: item.title })} className="w-full h-full object-contain mix-blend-multiply" loading="lazy" decoding="async" />}
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.handle}`} className="text-[14px] text-foreground hover:underline line-clamp-1 font-medium">
                      {item.title}
                    </Link>
                    {item.label && <p className="text-[12px] text-muted-foreground truncate">{item.label}</p>}
                  </div>
                  <span className="text-[14px] text-foreground font-medium flex-shrink-0">
                    {formatPrice(item.price.toString(), currencyCode)}
                  </span>
                </div>
              ))}
            </div>

            <div className="border-t border-border pt-4 space-y-2 mb-6">
              <div className="flex justify-between text-[16px]">
                <span className="font-semibold text-foreground">Total</span>
                <span className="font-bold text-foreground">{formatPrice(subtotal.toString(), currencyCode)}</span>
              </div>
            </div>

            <Button
              onClick={handleAddBundle}
              disabled={isLoading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-[52px] text-[14px] tracking-[0.15em] uppercase font-bold"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add all to cart
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
